import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@capacit-com/lara-law-shared-ui";
import { useUserManagementContext } from "@/providers/user-management/UserManagementContext";
import { SortableTableHeader } from "./SortableTableHeader";
import { INVITED_USERS_LAYOUT } from "../utils/tableColumnWidths";
import {
  UserManagementSkeletonRows,
  UserManagementTableHeader,
} from "./UserManagementTableColumns";
import {
  maxColumnSpan,
  type UserManagementColumn,
} from "../utils/userManagementColumns";
import { REFRESHING_ROWS_CLASS } from "../utils/tableLoadingState";
import { Badge } from "@capacit-com/lara-law-shared-ui";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { Edit, Mail, Redo, Trash2 } from "lucide-react";
import { formatDateToDanishLocaleOnlyDateShortMonthWithTime } from "@/utils/format";
import { InviteUserDialog } from "./InviteUserDialog";
import { cn } from "@/lib/utils";
import { logger } from "@/lib/logger";
import { ConfirmationDialog } from "@capacit-com/lara-law-shared-ui";
import useUserManagement from "@/hooks/useUserManagement";
import { useState } from "react";
import { InvitedUser } from "@/models/users/User";
import {
  showErrorToast,
  showSuccessToast,
} from "@/components/custom-toasts/toasts";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { Permission } from "@/lib/permissions/Permission.enum";
import { PermissionGate } from "@/lib/permissions/PermissionGate";
import { ChevronLeft, ChevronRight } from "lucide-react";
import UserManagementTableActions from "./UserManagementTableActions";
import { UserManagementTabs } from "../utils/userManagementTabs";

const InvitedUsersTable = () => {
  const {
    invitedUsers,
    setOpenInviteUserDialog,
    openInviteUserDialog,
    isLoadingInvitedUsers,
    isRefreshingInvitedUsers,
    invitedUsersPageNumber,
    setInvitedUsersPageNumber,
    invitedUsersSortBy,
    invitedUsersSortAscending,
    setInvitedUsersSort,
  } = useUserManagementContext();
  const { deleteInvitedUser, inviteUsers } = useUserManagement();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<InvitedUser | null>(null);
  const [userToEdit, setUserToEdit] = useState<InvitedUser | null>(null);

  const handleDeleteClick = (user: InvitedUser) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleEditInvitation = (user: InvitedUser) => {
    setOpenInviteUserDialog(true);
    setUserToEdit(user);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      await deleteInvitedUser.mutateAsync({
        invitationId: userToDelete.invitationId,
        userId: userToDelete.id,
      });
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (error) {
      logger.error("Failed to delete user invitation", {
        operation: "deleteUserInvitation",
        error,
        invitationId: userToDelete.invitationId,
        userId: userToDelete.id,
      });
    }
  };

  const handleResendInvitation = async (user: InvitedUser) => {
    try {
      const invitationDuration =
        (new Date(user.expiresAt).getTime() -
          new Date(user.createdAt).getTime()) /
        1000;
      await inviteUsers.mutateAsync({
        invitedUsersList: [
          {
            userId: user.id!,
            email: user.invitee.email,
            roles: user.roles,
            isReinvite: true,
          },
        ],
        connectionId: user.connectionId,
        expireDateInSeconds: invitationDuration,
      });
      showSuccessToast("Invitation sendt igen", "Invitation");
    } catch (error) {
      logger.error("Failed to resend user invitation", {
        operation: "resendUserInvitation",
        error,
        invitationId: user.invitationId,
        userId: user.id,
      });
      showErrorToast("Fejl ved sendelse af invitation", "Invitation");
    }
  };

  const handlePreviousPage = () => {
    if (invitedUsersPageNumber > 0) {
      setInvitedUsersPageNumber(invitedUsersPageNumber - 1);
    }
  };

  const handleNextPage = () => {
    if (invitedUsers.hasMore) {
      setInvitedUsersPageNumber(invitedUsersPageNumber + 1);
    }
  };

  const sortableHeader = (
    label: string,
    sortBy: Parameters<typeof setInvitedUsersSort>[0]
  ) => (
    <SortableTableHeader
      label={label}
      sortBy={sortBy}
      activeSortBy={invitedUsersSortBy}
      ascending={invitedUsersSortAscending}
      onSort={setInvitedUsersSort}
    />
  );

  const columns: UserManagementColumn[] = [
    {
      key: "email",
      width: INVITED_USERS_LAYOUT.columns.email,
      header: sortableHeader("Email", "email"),
      skeleton: <Skeleton className="h-4 w-full max-w-32" />,
    },
    {
      key: "inviter",
      width: INVITED_USERS_LAYOUT.columns.inviter,
      header: sortableHeader("Inviteret af", "inviter"),
      skeleton: <Skeleton className="h-4 w-full max-w-40" />,
    },
    {
      key: "status",
      width: INVITED_USERS_LAYOUT.columns.status,
      header: "Status",
      skeleton: (
        <div className="flex items-center gap-2 min-w-0">
          <Skeleton className="size-2 rounded-full shrink-0 aspect-square" />
          <Skeleton className="h-6 w-full max-w-[120px]" />
        </div>
      ),
    },
    {
      key: "createdAt",
      width: INVITED_USERS_LAYOUT.columns.createdAt,
      header: sortableHeader("Invitation oprettet", "createdAt"),
      skeleton: <Skeleton className="h-8 w-full max-w-[140px]" />,
    },
    {
      key: "expiresAt",
      width: INVITED_USERS_LAYOUT.columns.expiresAt,
      header: sortableHeader("Invitation udløbsdato", "expiresAt"),
      skeleton: <Skeleton className="h-8 w-full max-w-[140px]" />,
    },
    {
      key: "roles",
      width: INVITED_USERS_LAYOUT.columns.roles,
      header: "Roller",
      skeleton: <Skeleton className="h-6 w-full max-w-[80px]" />,
    },
    {
      key: "actions",
      width: INVITED_USERS_LAYOUT.columns.actions,
      permission: [
        Permission.ReadOrganizationConnection,
        Permission.CreateOrganizationInvitation,
        Permission.DeleteOrganizationInvitation,
      ],
      header: "Handlinger",
      skeleton: <Skeleton className="size-9" />,
    },
  ];

  return (
    <div className="flex flex-col h-full gap-4 min-h-0">
      {/* No search on this tab, so the row holds the table actions alone. */}
      <div className="flex items-center justify-end gap-4 min-h-9">
        <UserManagementTableActions tab={UserManagementTabs.INVITED_USERS} />
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto border rounded-xs">
        <Table
          removeOverflowX
          className="table-fixed"
          style={{ minWidth: INVITED_USERS_LAYOUT.minWidth }}
          aria-busy={isRefreshingInvitedUsers}
        >
          <UserManagementTableHeader columns={columns} />
          <TableBody
            className={cn(isRefreshingInvitedUsers && REFRESHING_ROWS_CLASS)}
          >
            {isLoadingInvitedUsers ? (
              <UserManagementSkeletonRows columns={columns} rows={10} />
            ) : invitedUsers.data?.length > 0 ? (
              invitedUsers.data?.map((user) => (
                <TableRow
                  key={user.id + "-" + user.invitationId}
                  className="group"
                >
                  <TableCell>
                    <div className="flex items-center gap-2 min-h-12 min-w-0">
                      <span className="truncate">{user.invitee.email}</span>
                    </div>
                  </TableCell>
                  <TableCell className="truncate">
                    {user.inviter.name}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div
                        className={cn(
                          "size-2 rounded-full shrink-0 aspect-square",
                          new Date(Date.now()) > new Date(user.expiresAt)
                            ? "bg-destructive shadow-[0_0_8px_rgba(153,27,27,0.8)]"
                            : "bg-warning shadow-[0_0_8px_rgba(245,158,11,0.8)]"
                        )}
                      />
                      <span className="flex items-center gap-2">
                        {new Date(Date.now()) > new Date(user.expiresAt) ? (
                          <>
                            Udløbet
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => handleResendInvitation(user)}
                                    id={`resend-invitation-${user.id}`}
                                    className="ml-1 size-7"
                                    disabled={inviteUsers.isPending}
                                  >
                                    <Redo className="w-3 h-3" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Send invitation igen
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </>
                        ) : (
                          "Afventer accept"
                        )}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {formatDateToDanishLocaleOnlyDateShortMonthWithTime(
                      new Date(user.createdAt)
                    )}
                  </TableCell>
                  <TableCell>
                    {formatDateToDanishLocaleOnlyDateShortMonthWithTime(
                      new Date(user.expiresAt)
                    )}
                  </TableCell>
                  <TableCell className="relative">
                    <div className="flex items-center justify-between">
                      <div className="flex flex-wrap gap-1">
                        {!user.roles || user.roles.length === 0 ? (
                          <Badge variant="secondary">Ingen rolle</Badge>
                        ) : (
                          user.roles.map((role) => {
                            return (
                              <Badge key={role.externalName} variant="outline">
                                {role.displayName}
                              </Badge>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <PermissionGate
                    allOf={[
                      Permission.ReadOrganizationConnection,
                      Permission.CreateOrganizationInvitation,
                      Permission.DeleteOrganizationInvitation,
                    ]}
                  >
                    <TableCell>
                      <div className="flex items-center gap-4">
                        <PermissionGate
                          allOf={[Permission.CreateOrganizationInvitation]}
                        >
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleEditInvitation(user)}
                            id={`resend-invitation-${user.id}`}
                            disabled={inviteUsers.isPending}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </PermissionGate>

                        <PermissionGate
                          allOf={[Permission.DeleteOrganizationInvitation]}
                        >
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleDeleteClick(user)}
                            id={`delete-user-${user.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </PermissionGate>
                      </div>
                    </TableCell>
                  </PermissionGate>
                </TableRow>
              ))
            ) : (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={maxColumnSpan(columns)}
                  className="text-center h-[calc(100vh-19rem)]"
                >
                  <div className="flex flex-col items-center justify-center gap-4">
                    Ingen inviterede brugere
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setOpenInviteUserDialog(true)}
                    >
                      {" "}
                      <Mail className="w-4 h-4 mr-1" /> Inviter bruger
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-end gap-4 px-2 pt-2 pb-1">
        <div className="text-sm text-muted-foreground">
          Side {invitedUsersPageNumber + 1}
          {invitedUsers.totalCount > 0 && (
            <>
              {" "}
              • {invitedUsers.totalCount} invitation
              {invitedUsers.totalCount !== 1 ? "er" : ""}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePreviousPage}
            disabled={invitedUsersPageNumber === 0 || isLoadingInvitedUsers}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Forrige
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleNextPage}
            disabled={!invitedUsers.hasMore || isLoadingInvitedUsers}
          >
            Næste
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
      <InviteUserDialog
        open={openInviteUserDialog}
        onOpenChange={setOpenInviteUserDialog}
        userToEdit={userToEdit}
        setUserToEdit={setUserToEdit}
      />
      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setUserToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        confirmPending={deleteInvitedUser.isPending}
        title="Slet invitation"
        description={`Invitationen til ${userToDelete?.invitee.email ?? ""} og brugeren bliver slettet.`}
        confirmText="Slet invitation"
        confirmIcon={<Trash2 className="w-4 h-4 mr-1" />}
        variant="destructive"
      />
    </div>
  );
};

export default InvitedUsersTable;
